import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Spin, Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { WithTranslation, withTranslation } from 'react-i18next';
import { ThunkDispatch } from 'redux-thunk';
import type { Action } from '@reduxjs/toolkit';

import helpCenterConstants from '../../constants/HelpCenterConstants.json';
import { withContainerWrapper } from '../ContainerWrapper';
import {
  fetchSyncLog,
  fetchOutOfSyncItems,
  fetchOutOfSyncItemsCount,
} from '../../actions/brand/synchronisation/index';
import { ApplicationState } from '../../reducers';
import withAntdContext, { WithAntdContextProps } from '../withAntdContext';

const { url: helpCenterUrl, articles } = helpCenterConstants;

const mapStateToProps = (state: ApplicationState) => {
  const selectedBrand = state.parent.brands.brands.find(
    brand => brand.id === state.parent.brands.selectedBrandId
  );
  return {
    brandId: state.parent.brands.selectedBrandId,
    selectedBrand,
    isSyncing: state.brand.synchronisation.isSyncing,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, void, Action>) => ({
  dispatch,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & WithTranslation & WithAntdContextProps;

class SynchronisationManager extends React.Component<Props> {
  poller?: NodeJS.Timeout;

  pollCount = 1;

  skipPollCount = 1;

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.isSyncing && this.props.isSyncing) {
      this.startLogPolling();
      this.showSyncNotification();
    }
    if (prevProps.brandId !== this.props.brandId && this.props.isSyncing) {
      this.closeSyncNotification(prevProps.brandId);
    }
    if (prevProps.isSyncing && !this.props.isSyncing) {
      this.endLogPolling();
      this.closeSyncNotification();
    }
  }

  componentWillUnmount() {
    if (this.poller) {
      clearInterval(this.poller);
    }
    this.closeSyncNotification();
  }

  showSyncNotification = () => {
    const { t, brandId, selectedBrand, appContext } = this.props;
    const { notification } = appContext;

    if (!selectedBrand) return;

    notification.open({
      key: `sync-${brandId}`,
      message: (
        <div>
          <span>{`${t('publishData:publishNotificationTitle')}:`}</span>
          <div>{selectedBrand.name}</div>
        </div>
      ),
      description: (
        <React.Fragment>
          <div>
            {t('publishData:publishInfo', {
              brandName: selectedBrand.name,
            })}
          </div>
          <Button
            style={{ padding: '0' }}
            type="link"
            href={`${helpCenterUrl}${articles['publish_data-status']}`}
            target="_blank"
            rel="noopener noreferrer"
            icon={<InfoCircleOutlined />}
          >
            {t('common:moreInfo')}
          </Button>
        </React.Fragment>
      ),
      placement: 'bottomRight',
      duration: 0,
      icon: <Spin size="small" />,
    });
  };

  closeSyncNotification = (brandId?: number) => {
    const { appContext } = this.props;
    const { notification } = appContext;

    const brandNotificationId = brandId || this.props.brandId;
    notification.destroy(`sync-${brandNotificationId}`);
  };

  startLogPolling = () => {
    this.pollCount = 1;
    this.skipPollCount = 1;
    this.poller = setInterval(() => {
      // skip poll by variable to allow dynamic intervals
      if (!(this.pollCount % this.skipPollCount)) this.fetchSyncLog();
      // increase poll interval from 5s to 30s every 5m
      if (!(this.pollCount % 60) && this.skipPollCount < 6) {
        this.skipPollCount++;
      }
      if (this.pollCount) {
        this.pollCount++;
      }
    }, 5000);
  };

  fetchSyncLog = () => {
    if (this.props.brandId) {
      this.props.dispatch(fetchSyncLog(this.props.brandId));
    }
  };

  endLogPolling = () => {
    if (this.poller) {
      clearInterval(this.poller);
    }
    this.props.dispatch(fetchOutOfSyncItems(this.props.brandId));
    this.props.dispatch(fetchOutOfSyncItemsCount(this.props.brandId));
  };

  render() {
    return null;
  }
}

export default connector(
  withContainerWrapper(withTranslation()(withAntdContext(SynchronisationManager)))
);
